import axios from "axios";
import _ from 'lodash';
import router from "../../router";
import {Storage} from '@capacitor/storage';
import i18n from "../../i18n";
import {Badge} from '@capawesome/capacitor-badge';
import {NativeBiometric} from "capacitor-native-biometric";

const auth = {
	state: {
		user: {},
		user_token: '',
		userChange: 0,
		isLoggedIn: false,
		shouldLogIn: true,
		stopAutomaticLogout: false,
		lastActiveTimestamp: 0,
		userDefaultId: false,
		intranetUser: false,
		inviteHash: false,
		inviteAgent: false,
		afterLoginAction: false,
		pendingAction: false,

		syncPercentage: 0,
		syncThreshold: 0,
		syncInterval: null,
		syncThresholdInterval: null,
		syncCompleted: false,
		syncLoading: false,

		chosenClient: {
			name: '',
			intranet_id: '',
			alliance: '',
		}
	},
	mutations: {
		SET_USER(state, user) {
			this._vm.$set(state, 'user', user);
			state.userChange++;
		},
		SET_USER_LOGGED_IN(state) {
			state.isLoggedIn = true;
		},
		SET_SHOULD_LOGIN(state, data) {
			state.shouldLogIn = data;
		},
		SET_USER_TOKEN(state, data) {
			state.user_token = data;
		},
		TOGGLE_LOGIN_POPUP(state) {
			state.showLoginPopup = !state.showLoginPopup;
		},
		SET_INTRANET_USER(state, data) {
			state.intranetUser = data;
		},
		SET_INVITE_AGENT(state, data) {
			state.inviteAgent = data;
		},
		SET_INVITE_HASH(state, data) {
			state.inviteHash = data;
		},
		SET_AFTER_LOGIN_ACTION(state, data) {
			state.afterLoginAction = data;
		},
		SET_PENDING_ACTION(state, data) {
			state.pendingAction = data;
		},
		ADD_NOTIFICATION(state, data) {
			state.user.unread_notifications.unshift(data);
		},
		CLEAR_NOT_SHOWN_NOTIFICATIONS(state) {
			state.user.not_shown_notifications_count = 0;
		},
		INCREASE_NOT_SHOWN_NOTIFICATIONS(state) {
			state.user.not_shown_notifications_count++;
		},
		REMOVE_NOTIFICATION(state, id) {
			state.user.unread_notifications = _.remove(state.user.unread_notifications, (item) => item.id !== id);
		},
		SET_STOP_AUTOMATIC_LOGOUT(state) {
			state.stopAutomaticLogout = true;
		},
		UNSET_STOP_AUTOMATIC_LOGOUT(state) {
			state.stopAutomaticLogout = false;
		},
		SET_LAST_ACTIVE_TIMESTAMP(state, data) {
			state.lastActiveTimestamp = data;
		},
		SET_INTRANET_ID(state, intranetId) {
			state.user.intranet_id = intranetId;
		},
		SET_SYNC_PERCENTAGE(state, percentage) {
			state.syncPercentage = Math.min(100, Math.max(0, percentage));
		},
		SET_SYNC_THRESHOLD(state, threshold) {
			state.syncThreshold = Math.min(100, Math.max(0, threshold));
		},
		SET_SYNC_COMPLETED(state, value) {
			state.syncCompleted = value;
		},
		SET_SYNC_LOADING(state, value) {
			state.syncLoading = value;
		},
	},
	actions: {
		chooseClient(context, data) {
			context.state.chosenClient.name = data.chosenClient.name;
			context.state.chosenClient.intranet_id = data.chosenClient.intranet_id;
			context.state.chosenClient.alliance = data.chosenClient.alliance;
			localStorage.setItem('chosen_client', JSON.stringify(data.chosenClient));

			if(data.chosenClient.intranet_id){
				axios.defaults.headers.common['Intranet-Id'] = data.chosenClient.intranet_id;
			}

			if(data.chosenClient.alliance){
				axios.defaults.headers.common['Alliance'] = data.chosenClient.alliance;
			}

			if (data.reload) {
				context.dispatch('reloadCurrentData');
			}

		},
		async removeDefaultUser(context) {
			await Storage.remove({key: 'default_user_id'});
			await Storage.remove({key: 'user_pin'});

			NativeBiometric.deleteCredentials({
				server: process.env.VUE_APP_CAP_ID_ANDROID
			});

			await context.dispatch('clearOfflineCards');

			context.state.userDefaultId = false;

			return true;
		},

		async setDefaultUserId(context, id) {
			return await Storage.set({
				key: 'default_user_id',
				value: id.toString(),
			});
		},
		async getDefaultUserId() {
			const item = await Storage.get({
				key: 'default_user_id',
			});

			if (!item.value) {
				return false;
			}

			return item;
		},

		async checkDefaultUserLogin(context) {
			if (!context.rootState.app.native) {
				return false;
			}

			const userId = await context.dispatch('getDefaultUserId');
			context.state.userDefaultId = userId.value;
		},
		async getUser(context) {
			return new Promise((resolve, reject) => {
				let urlParams = new URLSearchParams(window.location.search);
				if (urlParams.has('user-masquerade')) {
					reject();
					return;
				}

				context.dispatch('checkAutomaticLogoutStatus');
				if (!axios.defaults.headers.common['Authorization'] || !context.state.shouldLogIn) {
					reject();
					return;
				}

				context.commit('SET_APP_LOADING');
				axios.get('/api/user').then((response) => {
					context.commit('SET_USER', response.data.user);
					if (!response.data.user.active) {
						context.commit('OPEN_USER_ACTIVATION_POPUP');
					}
					if (!response.data.user.complete_registration) {
						context.commit('OPEN_COMPLETE_REGISTRATION_POPUP');
					}

					if (response.data.user.settings && typeof response.data.user.settings.dark_mode !== 'undefined') {
						context.commit('SET_DARK_MODE', response.data.user.settings.dark_mode);
					}

					context.commit('SET_USER_LOGGED_IN');

					let chosenClient = localStorage.getItem('chosen_client');
					if (chosenClient) {
						chosenClient = JSON.parse(chosenClient);
						context.dispatch('chooseClient', {
							chosenClient: chosenClient,
						});
					} else {
						let chosenClient = {
							name: context.state.user.full_name,
							intranet_id: context.state.user.intranet_id?.length ? context.state.user.intranet_id[0].intranet_id : '',
							alliance: context.state.user.intranet_id?.length ? context.state.user.intranet_id[0].alliance : context.state.user.alliance
						};
						context.dispatch('chooseClient', {
							chosenClient: chosenClient
						});
					}

					context.commit('UNSET_APP_LOADING');
					resolve();
				}).catch((error) => {
					localStorage.removeItem('user_token');
					context.commit('UNSET_APP_LOADING');
					reject(error);
				});
			});
		},
		processLogin(context, response) {
			localStorage.removeItem('last_active_timestamp');
			context.commit('SET_USER', response.data.user);
			if (!response.data.user.active) {
				context.commit('OPEN_USER_ACTIVATION_POPUP');
			}
			if (response.data.user.settings && typeof response.data.user.settings.dark_mode !== 'undefined') {
				context.commit('SET_DARK_MODE', response.data.user.settings.dark_mode);
			}
			if (!response.data.user.complete_registration) {
				context.commit('OPEN_COMPLETE_REGISTRATION_POPUP');
			}
			context.commit('SET_USER_LOGGED_IN');
			localStorage.setItem('user_token', response.data.user_token);
			axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.user_token}`;
			context.commit('SET_USER_TOKEN', response.data.user_token);

			if (context.rootState.app.native) {
				context.dispatch('setDefaultUserId', response.data.user.id);
				context.state.userDefaultId = response.data.user.id;
				context.dispatch('synchronizeBadges', response.data.user.not_shown_notifications_count);
				context.dispatch('syncCardsOffline');
			}

			context.dispatch('syncCheck');

			let chosenClient;
			if(response.data.user.intranet_id && response.data.user.intranet_id.length){
				chosenClient = {
					name: response.data.user.full_name,
					intranet_id: response.data.user.intranet_id[0].intranet_id,
					alliance: response.data.user?.intranet_id?.[0]?.alliance || response.data.user?.alliance
				};
			} else {
				chosenClient = {
					name: response.data.user.full_name,
					intranet_id: '',
					alliance: response.data.user?.alliance
				};
			}

			context.dispatch('chooseClient', {
				chosenClient: chosenClient
			});

			if (response.data.user.settings?.after_login_action) {
				context.commit('SET_PENDING_ACTION', response.data.user.settings.after_login_action);
			}

			router.push('/');
		},
		processPendingAction(context, action) {
			let [type, value] = action.split('|');
			context.state.pendingAction = false;
			context.commit('SET_AFTER_LOGIN_ACTION', false);

			switch(type){
				case 'online-service':
					context.dispatch('chooseOnlineService', value);
					break;
				default:
					break;
			}

			axios.delete('/api/user/after-login-action');
		},
		login(context, formData) {
			if (context.rootState.app.native) {
				formData.append('app_version', context.rootState.app.version);
			}

			return new Promise((resolve, reject) => {
				axios.post('/api/login', formData).then(response => {
					context.dispatch('processLogin', response);
					resolve();
				}).catch(error => {

					if (error?.response?.data?.message) {
						this._vm.$swal.fire({
							icon: 'error',
							title: 'Oops',
							text: error.response.data.message
						});
					}

					if (error?.response?.data?.errors) {
						this.errors = error.response.data.errors;
					}


					if (error?.response?.data?.action) {
						context.dispatch('removeDefaultUser');
					}

					reject();
				});
			});
		},
		nativeLogin(context, formData) {
			formData.append('app_version', context.rootState.app.version);

			return new Promise((resolve, reject) => {
				axios.post('/api/native-login', formData).then(response => {
					context.dispatch('processLogin', response);
					resolve();
				}).catch(error => {
					this._vm.$swal.fire({
						icon: 'error',
						title: 'Oops',
						text: error.response.data.message
					});

					if (error.response.data.errors) {
						this.errors = error.response.data.errors;
					}


					if (error?.response?.data?.action) {
						context.dispatch('closePopups');
						context.dispatch('removeDefaultUser');
					}

					reject(error);
				});
			});
		},
		checkAutomaticLogoutStatus(context) {
			if (!context.rootState.app.native) {
				return;
			}

			let syncIntranetId = localStorage.getItem('syncIntranetId');
			if (syncIntranetId) {
				localStorage.removeItem('syncIntranetId');
				return;
			}

			let lastActiveTimestamp = localStorage.getItem('last_active_timestamp');
			if (!lastActiveTimestamp) {
				return;
			}
			lastActiveTimestamp = parseInt(lastActiveTimestamp);

			let logoutTime = 120000;
			let currentTimestamp = +new Date();
			if ((currentTimestamp - lastActiveTimestamp >= logoutTime) && !context.state.stopAutomaticLogout) {
				context.commit('SET_SHOULD_LOGIN', false);
				context.dispatch('processLogOut');
			}
		},
		processLogOut(context) {
			localStorage.removeItem('last_active_timestamp');

			if (!context.state.isLoggedIn) {
				return false;
			}

			if (context.state.user.facebook_user_id) {
				//FacebookLogin.logout();
			}
			if (context.state.user.google_user_id) {
				if (context.rootState.app.native) {
					//todo logout
					//GoogleAuth.signOut();
				} else {
					let googleAuth = gapi.auth2.getAuthInstance();
					if (googleAuth) {
						googleAuth.signOut();
					}
				}
			}

			context.state.user = {};
			context.state.isLoggedIn = false;
			localStorage.removeItem('user_token');
			localStorage.removeItem('chosen_client');
			context.dispatch('closePopups');
			context.commit('SET_VIEWS_DEFAULT_STATE');
			router.push(i18n.t('routes.landing.path'));
			context.commit('SET_SHOULD_LOGIN', true);
			context.commit('SET_DARK_MODE', true);
		},
		logout(context) {
			context.commit('SET_APP_LOADING');
			axios.get('api/logout').then(() => {
				context.dispatch('processLogOut');
				context.commit('UNSET_APP_LOADING');
			});
		},
		async synchronizeBadges(context, badgesCount) {
			let permission = await Badge.checkPermissions();
			if (permission.display !== 'granted') {
				return;
			}
			await Badge.set({count: badgesCount});
		},

		syncStatus(context) {
			if (context.state.syncCompleted || context.state.syncLoading) {
				return;
			}

			context.commit('SET_SYNC_LOADING', true);
			axios.get('/api/user/sync-status').then((response) => {
				if (context.state.syncCompleted) {
					return;
				}

				const data = response.data;

				if (data.overall_progress > context.state.syncPercentage) {
					context.commit('SET_SYNC_PERCENTAGE', data.overall_progress);
				}

				const newThreshold = ((data.completed_components / data.total_components) * 100) - 5;
				context.commit('SET_SYNC_THRESHOLD', Math.max(0, newThreshold));

			}).catch(() => {
				// Continue with current progress on error
			}).finally(() => {
				context.commit('SET_SYNC_LOADING', false);
			});
		},

		syncCheck(context) {
			context.commit('SET_SYNC_PERCENTAGE', 0);
			context.commit('SET_SYNC_THRESHOLD', 15);
			context.commit('SET_SYNC_COMPLETED', false);

			context.state.syncInterval = setInterval(() => {
				context.dispatch('syncStatus');
			}, 5000);

			context.state.syncThresholdInterval = setInterval(() => {
				if (context.state.syncCompleted ||
				    context.state.syncPercentage >= context.state.syncThreshold ||
				    context.state.syncPercentage >= 100) {
					return;
				}
				context.commit('SET_SYNC_PERCENTAGE', context.state.syncPercentage + 1);
			}, 1000);

			setTimeout(() => {
				context.dispatch('syncStatus');
			}, 5000);

			axios.get('/api/user/sync-check').then((response) => {
				context.commit('SET_SYNC_COMPLETED', true);

				if (context.state.syncInterval) {
					clearInterval(context.state.syncInterval);
					context.state.syncInterval = null;
				}

				if (context.state.syncThresholdInterval) {
					clearInterval(context.state.syncThresholdInterval);
					context.state.syncThresholdInterval = null;
				}

				context.commit('SET_SYNC_PERCENTAGE', 100);

				if (response.data.reset_timestamps) {
					context.dispatch('resetTimestamps');
				}

				if (response.data.user) {
					context.commit('SET_USER', response.data.user);
				}
			});
		},
	}
};

export default auth;